import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from "lucide-react";
import type { PropsWithChildren } from "react";
import { isValidUrl } from "common/lib/isValidUrl";
import { CallToActionStyle } from "common/types/graphql";
import { Heading } from "common/components/Heading";
import clsx from "classnames";
import { Button } from "common/components/Button";
import { useHasBeenInViewport } from "common/hooks/interaction/useHasBeenInViewport";
import { useEffect } from "react";
import { CallToActionTrackingEventType, trackCallToActionEvent } from "modules/promos/lib/tracking";
import { trackCallToActionEngaged, trackCallToActionViewed } from "common/lib/tcEventCollector";
import { useTrackLinkClick } from "common/hooks/interaction/useTrackLinkClick";
export interface CallToActionProps {
  href: string;
  title?: string;
  text: string;
  style: CallToActionStyle;
  analyticsId: string;
}
export const CallToAction = t0 => {
  "use memo";

  const $ = _c(30);
  const {
    href,
    title,
    text,
    style: t1,
    analyticsId
  } = t0;
  const style = t1 === undefined ? CallToActionStyle.Default : t1;
  const sanitizedHref = isValidUrl(href) ? href : undefined;
  const {
    setRef,
    hasBeenInViewport
  } = useHasBeenInViewport(0);
  let t2;
  let t3;
  if ($[0] !== analyticsId || $[1] !== hasBeenInViewport) {
    t2 = () => {
      if (analyticsId && analyticsId.length > 0 && hasBeenInViewport) {
        trackCallToActionEvent(CallToActionTrackingEventType.Impression, analyticsId);
        trackCallToActionViewed(analyticsId);
      }
    };
    t3 = [hasBeenInViewport, analyticsId];
    $[0] = analyticsId;
    $[1] = hasBeenInViewport;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== analyticsId) {
    t4 = () => {
      trackCallToActionEvent(CallToActionTrackingEventType.Engagement, analyticsId);
      trackCallToActionEngaged(analyticsId);
    };
    $[4] = analyticsId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const trackClick = useTrackLinkClick(href, analyticsId, t4);
  if (title) {
    const t5 = style === CallToActionStyle.Default;
    const t6 = style === CallToActionStyle.Donate;
    const t7 = style === CallToActionStyle.Newsletter;
    let t8;
    if ($[6] !== t5 || $[7] !== t6 || $[8] !== t7) {
      t8 = clsx("my-6 flex flex-col items-center gap-6 px-6 py-8 text-center", {
        "bg-gray-50 text-black": t5,
        "bg-red-500 text-white": t6,
        "bg-indigo-600 text-white": t7
      });
      $[6] = t5;
      $[7] = t6;
      $[8] = t7;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    let t9;
    if ($[10] !== title) {
      t9 = <Heading level={3} styledAsLevel={4}>{title}</Heading>;
      $[10] = title;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    const t10 = style !== CallToActionStyle.Default;
    let t11;
    if ($[12] !== href || $[13] !== t10 || $[14] !== text || $[15] !== trackClick) {
      t11 = <Button size="large" onDark={t10} primary={true} href={href} onClick={trackClick} onAuxClick={trackClick}>{text}</Button>;
      $[12] = href;
      $[13] = t10;
      $[14] = text;
      $[15] = trackClick;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== setRef || $[18] !== t11 || $[19] !== t8 || $[20] !== t9) {
      t12 = <div className={t8} ref={setRef}>{t9}{t11}</div>;
      $[17] = setRef;
      $[18] = t11;
      $[19] = t8;
      $[20] = t9;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    return t12;
  }
  let t5;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowRight className="inline size-5 flex-none" />;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== sanitizedHref || $[24] !== text || $[25] !== trackClick) {
    t6 = <a href={sanitizedHref} className="inline-flex gap-3 text-sm font-bold underline hover:text-indigo-600" onClick={trackClick} onAuxClick={trackClick}>{text} {t5}</a>;
    $[23] = sanitizedHref;
    $[24] = text;
    $[25] = trackClick;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== setRef || $[28] !== t6) {
    t7 = <div className="my-4" ref={setRef}>{t6}</div>;
    $[27] = setRef;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
};
export type CallToActionComponent = typeof CallToAction;